// src/modules/profile/pages/ProfilePage/index.js
import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import ProfileView from '../../components/ProfileView';
import ProfileEdit from '../../components/ProfileEdit';
import Loading from '../../../../shared/components/Loading';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { useAlert } from '../../../../shared/hooks/useAlert';

const ProfilePage = () => {
    const [profile, setProfile] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { isWarmup } = useWarmupTimer(isLoading);
    const [isEditing, setIsEditing] = useState(false);
    const { username } = useParams();
    const { user, hasPermission } = useAuth();
    const service = useService();
    const { alert, showAlert, showSuccess, showError } = useAlert();

    // Calculate isOwnProfile after profile is loaded, comparing usernames
    // Note: user.Username (capital U) comes from Cognito, profile.user.username (lowercase) from our DB
    const isOwnProfile = user?.Username && profile?.user?.username
        ? user.Username === profile.user.username
        : false;

    const canEditProfile = isOwnProfile || hasPermission('users:editProfile');

    useEffect(() => {
        const loadProfile = async () => {
            try {
                const data = await service.getProfile(username);
                setProfile(data);
            } catch (error) {
                showError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        loadProfile();
    }, [service, username, showError]);


    const handlePermissionsUpdated = (newPermissions) => {
        setProfile(prev => ({
            ...prev,
            user: { ...prev.user, permissions: newPermissions },
        }));
    };

    const handleUpdateProfile = async (updatedData) => {
        try {
            await service.updateProfile(username, updatedData);
            // Reload the full profile after update
            const refreshedProfile = await service.getProfile(username);
            setProfile(refreshedProfile);
            setIsEditing(false);
            showSuccess('Profile updated successfully');
        } catch (error) {
            showError(error.message);
        }
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (!profile) return <div>Profile not found</div>;

    return (
        <Container className="mt-3">
            {isEditing && canEditProfile ? (
                <ProfileEdit
                    profile={profile}
                    onSave={handleUpdateProfile}
                    onCancel={() => setIsEditing(false)}
                    alert={alert}
                    showAlert={showAlert}
                />
            ) : (
                <ProfileView
                    profile={profile}
                    isOwnProfile={isOwnProfile}
                    canEditProfile={canEditProfile}
                    onEdit={() => setIsEditing(true)}
                    onPermissionsUpdated={handlePermissionsUpdated}
                />
            )}
        </Container>
    );
};

export default ProfilePage;
